import React, { useState } from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { toast } from "react-toastify";
import SettingsContent from "../../shared/SettingsContent";
import SettingsPageHeader from "../../shared/SettingsPageHeader";
import SettingsPrimaryButton from "../../shared/SettingsPrimaryButton";
import SettingsSecondaryButton from "../../shared/SettingsSecondaryButton";
import { COLORS, FONT } from "../../shared/styles";
import {
  CancelBellIcon,
  CreditCardIcon,
  DownloadIcon,
  TickCircleIcon,
} from "../icons";

const sectionTitleSx = {
  color: COLORS.textPrimary,
  fontFamily: FONT.family,
  fontSize: "20px",
  fontWeight: 500,
  lineHeight: "34px",
  m: 0,
};

const PLAN_FEATURES = [
  "Up to 10 agents",
  "Unlimited conversations",
  "Advanced analytics",
  "Priority support",
];

const ADD_ONS = [
  {
    id: "help-desk",
    title: "Help Desk Module",
    description: "Full ticketing system with email integration and SLA management",
    price: "$49/month",
    subscribed: true,
  },
  {
    id: "additional-groups",
    title: "Additional Groups",
    description: "Add 5 more support groups to organize your team",
    price: "$29/month",
    subscribed: false,
  },
  {
    id: "advanced-analytics",
    title: "Advanced Analytics",
    description: "Detailed reports, custom dashboards, and data export",
    price: "$39/month",
    subscribed: false,
  },
];

const BILLING_HISTORY = [
  { id: "INV-2026-04", date: "2026-04-01", amount: "$197.00", status: "Paid" },
  { id: "INV-2026-03", date: "2026-03-01", amount: "$197.00", status: "Paid" },
  { id: "INV-2026-02", date: "2026-02-01", amount: "$197.00", status: "Paid" },
];

function PlanFeatureItem({ label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
      <TickCircleIcon />
      <Typography
        sx={{
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "24px",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function AddOnCard({ addon, onToggle }) {
  const isSubscribed = addon.subscribed;

  return (
    <Box
      sx={{
        width: "100%",
        borderRadius: "8px",
        border: `1px solid ${COLORS.border}`,
        background: COLORS.white,
        p: "16px",
        position: "relative",
      }}
    >
      <Box sx={{ position: "absolute", top: "16px", right: "16px" }}>
        {isSubscribed ? (
          <Button
            onClick={() => onToggle(addon.id)}
            sx={{
              height: "24px",
              px: "12px",
              width: "96px",
              borderRadius: "8px",
              border: `1px solid #D5DCE1`,
              background: COLORS.white,
              color: COLORS.error,
              fontFamily: FONT.family,
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                background: "#FFF1F0",
                borderColor: "#D5DCE1",
                boxShadow: "none",
              },
            }}
          >
            Unsubscribe
          </Button>
        ) : (
          <Button
            onClick={() => onToggle(addon.id)}
            sx={{
              height: "24px",
              px: "12px",
              width: "80px",
              borderRadius: "8px",
              border: "none",
              background: COLORS.primary,
              color: COLORS.white,
              fontFamily: FONT.family,
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "18px",
              textTransform: "none",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              "&:hover": {
                background: "#127EDE",
                boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              },
            }}
          >
            Subscribe
          </Button>
        )}
      </Box>

      <Typography
        sx={{
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "16px",
          fontWeight: 500,
          lineHeight: "22px",
          pr: "120px",
        }}
      >
        {addon.title}
      </Typography>
      <Typography
        sx={{
          mt: "4px",
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "18px",
          pr: "120px",
        }}
      >
        {addon.description}
      </Typography>
      <Typography
        sx={{
          mt: "12px",
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 500,
          lineHeight: "18px",
        }}
      >
        {addon.price}
      </Typography>
    </Box>
  );
}

function PlanBillingPage() {
  const [addons, setAddons] = useState(ADD_ONS);

  const handleToggleAddon = (id) => {
    setAddons((prev) =>
      prev.map((addon) =>
        addon.id === id ? { ...addon, subscribed: !addon.subscribed } : addon,
      ),
    );
    const addon = addons.find((item) => item.id === id);
    if (addon) {
      toast.info(
        addon.subscribed
          ? `${addon.title} unsubscribed.`
          : `${addon.title} subscribed.`,
      );
    }
  };

  return (
    <SettingsContent>
      <SettingsPageHeader
        title="Plan & Billing"
        description="Manage your subscription, add-ons, and payment information"
      />

      <Box sx={{ mt: "32px" }}>
        <Typography component="h3" sx={sectionTitleSx}>
          Current Plan
        </Typography>

        <Box
          sx={{
            mt: "16px",
            width: "100%",
            borderRadius: "8px",
            border: `1px solid ${COLORS.primary}`,
            background: "#F3FBFF",
            boxShadow: "0 1px 2px 0 rgba(20, 141, 253, 0.10)",
            p: "16px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: COLORS.textPrimary,
                  fontFamily: FONT.family,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "24px",
                }}
              >
                Professional Plan
              </Typography>
              <Typography
                sx={{
                  mt: "4px",
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                Perfect for growing support teams
              </Typography>
            </Box>
            <Box sx={{ textAlign: "right", flexShrink: 0 }}>
              <Typography
                sx={{
                  color: COLORS.textPrimary,
                  fontFamily: FONT.family,
                  fontSize: "28px",
                  fontWeight: 500,
                  lineHeight: "18px",
                }}
              >
                $99
              </Typography>
              <Typography
                sx={{
                  mt: "4px",
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "12px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                per month
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              mt: "16px",
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: "8px 24px",
            }}
          >
            {PLAN_FEATURES.map((feature) => (
              <PlanFeatureItem key={feature} label={feature} />
            ))}
          </Box>

          <Box
            sx={{
              mt: "16px",
              width: "100%",
              height: "1px",
              background: "#D6EDFF",
            }}
          />

          <Box sx={{ mt: "16px" }}>
            <SettingsPrimaryButton
              sx={{ width: "144px" }}
              onClick={() => toast.info("Update plan will be implemented here.")}
            >
              Update Plan
            </SettingsPrimaryButton>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: "32px" }}>
        <Typography component="h3" sx={sectionTitleSx}>
          Add-ons
        </Typography>
        <Box sx={{ mt: "16px", display: "flex", flexDirection: "column", gap: "16px" }}>
          {addons.map((addon) => (
            <AddOnCard key={addon.id} addon={addon} onToggle={handleToggleAddon} />
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: "32px" }}>
        <Typography component="h3" sx={sectionTitleSx}>
          Payment Method
        </Typography>
        <Box
          sx={{
            mt: "16px",
            width: "100%",
            minHeight: "80px",
            borderRadius: "8px",
            border: `1px solid ${COLORS.border}`,
            background: COLORS.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            px: "16px",
            py: "16px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px", minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#F9FAFA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CreditCardIcon />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: COLORS.textPrimary,
                  fontFamily: FONT.family,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "22px",
                }}
              >
                Visa ending in 4242
              </Typography>
              <Typography
                sx={{
                  mt: "2px",
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                Expires 12/2027
              </Typography>
            </Box>
          </Box>
          <SettingsSecondaryButton
            sx={{
              height: "24px",
              px: "16px",
              width: "64px",
              color: COLORS.textSecondary,
              fontSize: "12px",
              fontWeight: 500,
              lineHeight: "22px",
              flexShrink: 0,
            }}
            onClick={() => toast.info("Update payment method will be implemented here.")}
          >
            Update
          </SettingsSecondaryButton>
        </Box>
      </Box>

      <Box sx={{ mt: "32px" }}>
        <Typography component="h3" sx={sectionTitleSx}>
          Billing History
        </Typography>
        <Box sx={{ mt: "16px", width: "100%" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.4fr 1fr 1fr 0.8fr 40px",
              gap: "8px",
              alignItems: "center",
              pb: "8px",
            }}
          >
            {["Invoice", "Date", "Amount", "Status"].map((header) => (
              <Typography
                key={header}
                sx={{
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: "18px",
                }}
              >
                {header}
              </Typography>
            ))}
            <Box />
          </Box>

          {BILLING_HISTORY.map((row, index) => (
            <Box
              key={row.id}
              sx={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr 1fr 0.8fr 40px",
                gap: "8px",
                alignItems: "center",
                py: "12px",
                borderTop: index === 0 ? `1px solid ${COLORS.borderLight}` : "none",
                borderBottom: `1px solid ${COLORS.borderLight}`,
              }}
            >
              <Typography
                sx={{
                  color: COLORS.textPrimary,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "18px",
                }}
              >
                {row.id}
              </Typography>
              <Typography
                sx={{
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {row.date}
              </Typography>
              <Typography
                sx={{
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "18px",
                }}
              >
                {row.amount}
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 48,
                  height: 24,
                  px: "8px",
                  width: "48px",
                  borderRadius: "50px",
                  border: "1px solid #52C41A",
                  background: "#F6FFED",
                }}
              >
                <Typography
                  sx={{
                    color: "#52C41A",
                    fontFamily: FONT.family,
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: "18px",
                  }}
                >
                  {row.status}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => toast.info(`Downloading ${row.id}...`)}
                sx={{ p: 0, width: 24, height: 24 }}
              >
                <DownloadIcon />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={{ mt: "32px" }}>
        <Box
          sx={{
            width: "100%",
            borderRadius: "8px",
            border: "1px solid #FFA39E",
            background: "#FFF1F0",
            p: "16px",
          }}
        >
          <Box sx={{ display: "flex", gap: "16px" }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                background: "#FFCCC7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <CancelBellIcon />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: COLORS.textPrimary,
                  fontFamily: FONT.family,
                  fontSize: "16px",
                  fontWeight: 500,
                  lineHeight: "22px",
                }}
              >
                Cancel Subscription
              </Typography>
              <Typography
                sx={{
                  mt: "4px",
                  color: COLORS.textSecondary,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "24px",
                }}
              >
                Canceling will downgrade your account to the free plan at the end of the
                current billing period.
              </Typography>
              <Typography
                component="button"
                onClick={() => toast.info("Cancel subscription will be implemented here.")}
                sx={{
                  mt: "12px",
                  p: 0,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: COLORS.error,
                  fontFamily: FONT.family,
                  fontSize: "14px",
                  fontWeight: 500,
                  lineHeight: "18px",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                Cancel my subscription
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </SettingsContent>
  );
}

export default PlanBillingPage;
